/*
 * Password generator for the admin user form.
 *
 * These passwords are created by one person and handed to another (email,
 * chat), then typed once, so the design targets that path rather than a
 * password manager's:
 *
 *   - CRYPTO randomness only. `Math.random()` is seeded, predictable and
 *     unsuitable for anything a login depends on; this uses
 *     crypto.getRandomValues with rejection sampling so no character is more
 *     likely than another (taking `random % length` biases the first
 *     `2^32 % length` characters, which is exactly the sort of quiet weakness
 *     that never shows up in testing).
 *   - NO AMBIGUOUS GLYPHS. 0/O, 1/l/I are dropped: a password that arrives by
 *     email gets retyped, and "was that a one or an ell?" costs a support
 *     round trip. Losing 5 of 62 alphanumerics costs ~0.4 bits per character —
 *     paid back by the length below.
 *   - PUNCTUATION KEPT SIMPLE — dots, commas, brackets and a few common
 *     symbols. Deliberately no quotes, backslashes, backticks, pipes, `$` or
 *     `&`: those get mangled by shells, URLs and CSV exports.
 *   - EVERY CLASS PRESENT. One upper, one lower, one digit and one symbol are
 *     placed first, then the rest is filled and the whole thing shuffled — so
 *     the guarantee never shows up as a predictable position.
 *
 * 14 characters over this 76-character alphabet is ~87 bits of entropy, far
 * beyond anything bcrypt-cost-12 hashes need to resist offline attack.
 */

const UPPER = 'ABCDEFGHJKLMNPQRSTUVWXYZ'; // no I, O
const LOWER = 'abcdefghijkmnopqrstuvwxyz'; // no l
const DIGITS = '23456789'; // no 0, 1
const SYMBOLS = '.,-_()[]!?@#%+=';

const ALPHABET = UPPER + LOWER + DIGITS + SYMBOLS;

/** Default length. Comfortable to transmit, still far above the 8-char minimum. */
export const DEFAULT_PASSWORD_LENGTH = 14;

/**
 * A uniformly random integer in [0, max) from the CSPRNG.
 *
 * Rejection sampling: draw 32-bit values and discard the tail that would make
 * the modulo uneven. The expected number of draws is under 2 for every `max`
 * used here.
 */
function randomBelow(max: number): number {
  const limit = Math.floor(0xffffffff / max) * max;
  const buf = new Uint32Array(1);
  for (;;) {
    crypto.getRandomValues(buf);
    if (buf[0] < limit) return buf[0] % max;
  }
}

function pick(chars: string): string {
  return chars[randomBelow(chars.length)];
}

/** Fisher–Yates, driven by the CSPRNG rather than Math.random. */
function shuffle(chars: string[]): string[] {
  for (let i = chars.length - 1; i > 0; i--) {
    const j = randomBelow(i + 1);
    [chars[i], chars[j]] = [chars[j], chars[i]];
  }
  return chars;
}

/**
 * Generate a password: letters, digits and simple punctuation, with at least
 * one of each class. Throws below length 4, where the per-class guarantee
 * cannot hold.
 */
export function generatePassword(length: number = DEFAULT_PASSWORD_LENGTH): string {
  if (!Number.isInteger(length) || length < 4) {
    throw new Error('password length must be an integer of at least 4 (one character per class)');
  }
  const chars = [pick(UPPER), pick(LOWER), pick(DIGITS), pick(SYMBOLS)];
  while (chars.length < length) chars.push(pick(ALPHABET));
  return shuffle(chars).join('');
}

/** The alphabet, exported for tests and for the form's hint text. */
export const PASSWORD_ALPHABET = ALPHABET;
